/*
 * Лента истории человека: касания по времени и полоса готовности.
 *
 * 🔴 Прочерк и ноль — разные вещи. `readiness.measured === false` означает,
 * что сверка не запускалась ни разу; ноль означал бы «запускалась и дала
 * ноль». Полоса рисует прочерк, а не пустую шкалу: адвокату важно отличать
 * «не смотрели» от «смотрели и пусто».
 *
 * 🔴 Пустая лента говорит словами, а не выглядит пустотой. И `note` из ответа
 * выводится всегда: сейчас он сообщает, что документов в ленте нет не потому,
 * что их нет у клиента, а потому что сканы пока не сохраняются.
 */
import React from 'react';
import './HistoryTimeline.css';
// 🔴 ВТОРОГО ДЕРЖАТЕЛЯ МЕТОК БОЛЬШЕ НЕТ. Локальный словарь жил здесь и разошёлся
//    бы с новым экраном молча — на первой же правке одной из копий. Метки
//    перенесены в config/historyEvents.js БУКВА В БУКВУ, включая мёртвую
//    `checklist_verified` (писателей 0): отрисовка этой страницы у fitenko и у
//    MLG обязана не измениться ни на символ, и это доказывают ворота слов
//    (slovar-ist.py, С3 — посимвольное равенство на объединении ключей).
import { metkaTipa } from '../../config/historyEvents';

const formatWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const ReadinessBar = ({ readiness }) => {
    if (!readiness || readiness.measured !== true) {
        return (
            <div className="history-readiness history-readiness--unmeasured">
                <div className="history-readiness__row">
                    <span className="history-readiness__label">Готовность к подаче</span>
                    <span className="history-readiness__value">—</span>
                </div>
                <p className="history-readiness__hint">
                    Сверка не запускалась ни разу. Прочерк — это не ноль процентов.
                </p>
            </div>
        );
    }
    const pct = typeof readiness.completion === 'number' ? readiness.completion : null;
    return (
        <div className="history-readiness">
            <div className="history-readiness__row">
                <span className="history-readiness__label">Готовность к подаче</span>
                <span className="history-readiness__value">{pct === null ? '—' : `${pct}%`}</span>
            </div>
            <div className="history-readiness__track">
                <div className="history-readiness__fill" style={{ width: `${pct || 0}%` }} />
            </div>
            <div className="history-readiness__meta">
                <span>Приложено: {readiness.attached === null || readiness.attached === undefined
                    ? '—' : `${readiness.attached}%`}</span>
                <span>Риск: {readiness.risk_level || '—'}</span>
                <span>К подаче: {readiness.ready_for_filing === null
                    || readiness.ready_for_filing === undefined
                    ? '—' : (readiness.ready_for_filing ? 'да' : 'нет')}</span>
            </div>
            {readiness.last_action && (
                <p className="history-readiness__hint">Последнее действие: {readiness.last_action}</p>
            )}
        </div>
    );
};

const HistoryTimeline = ({ history, loading, error }) => {
    if (loading) return <div className="history-timeline__state">Загружаю историю…</div>;
    if (error) {
        return (
            <div className="history-timeline__state history-timeline__state--error">
                История не загрузилась: {error}
            </div>
        );
    }
    if (!history) return null;

    const events = history.events || [];

    return (
        <section className="history-timeline">
            <div className="history-timeline__header">
                <h2>История</h2>
                <span className="history-timeline__count">
                    {events.length === 0 ? 'касаний пока не записано' : `${events.length} записей`}
                </span>
            </div>

            <ReadinessBar readiness={history.readiness} />

            {events.length === 0 ? (
                <p className="history-timeline__empty">
                    Ни одного касания в ленте. Это значит, что записей нет в базе, —
                    а не что с человеком не общались.
                </p>
            ) : (
                <ol className="history-timeline__list">
                    {events.map((e) => (
                        <li key={e.id} className={`history-event history-event--${e.event_type}`}>
                            <div className="history-event__when">{formatWhen(e.created_at)}</div>
                            <div className="history-event__body">
                                <span className="history-event__type">
                                    {metkaTipa(e.event_type)}
                                </span>
                                <span className="history-event__title">{e.title}</span>
                                {e.description && (
                                    <p className="history-event__desc">{e.description}</p>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            )}

            {history.note && <p className="history-timeline__note">{history.note}</p>}
        </section>
    );
};

export default HistoryTimeline;
